import { useEffect, useRef, useState, useMemo } from "react";
import { Box, Card, Text, Spinner } from "@shopify/polaris";

interface WidgetPreviewProps {
  shop: string;
  branding: {
    programName: string;
    brandColor: string;
    ctaButtonColor: string;
    themeMode: string;
  };
  colors: any;
}

// Convert camelCase colors to snake_case format expected by widget
const convertColorsToWidgetFormat = (colors: any): any => {
  if (!colors) return {};

  return {
    card_bg_color: colors.cardBgColor || "#ffffff",
    card_body_text_color: colors.cardBodyTextColor || "#333333",
    widget_header_bg_color: colors.widgetHeaderBgColor || "#f5f5f5",
    widget_header_title_color: colors.widgetHeaderTitleColor || "#000000",
    widget_header_subtitle_color: colors.widgetHeaderSubtitleColor || "#666666",
    customer_info_bg_color: colors.customerInfoBgColor || "#fafafa",
    customer_label_color: colors.customerLabelColor || "#000000",
    customer_value_color: colors.customerValueColor || "#333333",
    points_display_bg_color: colors.pointsDisplayBgColor || "#007bff",
    points_number_color: colors.pointsNumberColor || "#ffffff",
    points_label_color: colors.pointsLabelColor || "#ffffff",
    discounts_section_bg_color: colors.discountsSectionBgColor || "#ffffff",
    discounts_section_title_color:
      colors.discountsSectionTitleColor || "#333333",
    discount_item_bg_color: colors.discountItemBgColor || "#f9f9f9",
    discount_item_title_color: colors.discountItemTitleColor || "#000000",
    discount_item_subtitle_color: colors.discountItemSubtitleColor || "#555555",
    discount_button_bg_color: colors.discountButtonBgColor || "#ffcc00",
    discount_button_text_color: colors.discountButtonTextColor || "#000000",
  };
};

const WidgetPreview: React.FC<WidgetPreviewProps> = ({
  shop,
  branding,
  colors,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const drawerRef = useRef<any>(null);
  const buttonRef = useRef<any>(null);
  const isInitializedRef = useRef(false);

  // Convert colors to widget format - memoized to prevent unnecessary re-renders
  const widgetColors = useMemo(() => convertColorsToWidgetFormat(colors), [colors]);
  
  // Extract texts from colors - texts come along with colors from the API
  const widgetTexts = useMemo(() => {
    if (!colors) return {};
    return {
      guestHeaderTitle: colors.guestHeaderTitle || null,
      guestHeaderSubtitle: colors.guestHeaderSubtitle || null,
      guestBenefitsTitle: colors.guestBenefitsTitle || null,
      guestJoinButtonText: colors.guestJoinButtonText || null,
      rewardsHeaderSubtitle: colors.rewardsHeaderSubtitle || null,
      pointsLabelText: colors.pointsLabelText || null,
      rewardsSectionTitle: colors.rewardsSectionTitle || null,
      applyButtonText: colors.applyButtonText || null,
      appliedButtonText: colors.appliedButtonText || null,
      needMorePointsText: colors.needMorePointsText || null,
      installCardButtonText: colors.installCardButtonText || null,
      noRewardsText: colors.noRewardsText || null,
      noRewardsSubtitle: colors.noRewardsSubtitle || null,
      successTitle: colors.successTitle || null,
      goToCheckoutText: colors.goToCheckoutText || null,
      continueShoppingText: colors.continueShoppingText || null,
      remainingBalanceText: colors.remainingBalanceText || null,
    };
  }, [colors]);

  // Check if all required data is available (not null)
  const hasRequiredData = useMemo(() => {
    if (!branding) return false;
    if (!branding.programName || !branding.brandColor || !branding.themeMode) return false;
    if (!colors || Object.keys(colors).length === 0) return false;
    // Check if colors object has at least one valid color value
    const keys = Object.keys(colors);
    return keys.length > 0 && keys.some(key => colors[key] != null && colors[key] !== '');
  }, [branding, colors]);

  // Mock data - defined outside to avoid recreation
  const mockCustomerEmail = "preview@example.com";
  const mockWalletData = useMemo(() => ({
    id: "PREVIEW-SERIAL-1234567890", // Serial number for install card button
    balance: 2721,
    customer: {
      id: "preview-customer-id",
      email: mockCustomerEmail,
    },
    availableRewardTiers: [
      {
        id: "1",
        name: "$10 off coupon for the next purchase",
        type: 1,
        value: 10,
        threshold: 25,
        points: 25,
        valueLimit: 10,
      },
      {
        id: "2",
        name: "$20 off coupon for the next purchase",
        type: 1,
        value: 20,
        threshold: 50,
        points: 50,
        valueLimit: 20,
      },
    ],
  }), []);

  const mockActiveDiscount = useMemo(() => ({
    id: 1,
    code: "LOYALTY1D834EE3",
    discount_type: "AMOUNT",
    discount_value: 20,
    points_deducted: 50,
    expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    created_at: new Date().toISOString(),
    shop_domain: shop,
    discount_template_id: "2",
  }), [shop]);

  // Load preview script that mounts components directly
  const loadPreviewScript = () => {
    return new Promise<any>((resolve, reject) => {
      // Check if preview is already loaded
      if ((window as any).StickyCardsPreviewModule) {
        resolve((window as any).StickyCardsPreviewModule);
        return;
      }

      // Try to import the preview module using alias
      // @ts-ignore - preview module is in widget directory
      import("@widget/preview")
        .then((module: any) => {
          (window as any).StickyCardsPreviewModule = module;
          resolve(module);
        })
        .catch((err) => {
          console.warn("Failed to load preview module:", err);
          reject(err);
        });
    });
  };

  // Initialize preview only once - wait for all required data to be loaded
  useEffect(() => {
    const container = containerRef.current;
    // Don't initialize if container is not ready, already initialized, or required data is not available
    if (!container || isInitializedRef.current) {
      if (!hasRequiredData) {
        setIsLoading(true);
      }
      return;
    }
    
    // Wait for all required data (branding and colors) to be available
    if (!hasRequiredData) {
      setIsLoading(true);
      return;
    }

    let isMounted = true;

    const initPreview = async () => {
      try {
        if (!isMounted) return;
        setIsLoading(true);
        setError(null);

        // Clear container and create layout with two sections
        container.innerHTML = "";
        
        // Create drawer container (left side)
        const drawerWrapper = document.createElement("div");
        drawerWrapper.style.cssText = "flex: 1; display: flex; align-items: center; justify-content: center; padding: 20px;";
        drawerWrapper.id = "preview-drawer-container";
        container.appendChild(drawerWrapper);
        
        // Create button container (right side)
        const buttonWrapper = document.createElement("div");
        buttonWrapper.style.cssText = "flex: 1; display: flex; align-items: center; justify-content: center; padding: 20px; position: relative;";
        buttonWrapper.id = "preview-button-container";
        container.appendChild(buttonWrapper);

        // Load and use preview module
        try {
          const previewModule = await loadPreviewScript();

          // Small delay to ensure DOM is ready and HMR has settled
          await new Promise((resolve) => setTimeout(resolve, 50));

          // Verify container still exists
          if (
            !isMounted ||
            !containerRef.current ||
            containerRef.current !== container
          ) {
            if (isMounted) setIsLoading(false);
            return;
          }

          // Ensure container is a valid HTMLElement and in the DOM
          if (!(container instanceof HTMLElement)) {
            throw new Error("Container is not a valid HTMLElement");
          }

          if (!container.isConnected) {
            throw new Error("Container is not connected to the DOM");
          }

          // Get drawer and button containers
          const drawerContainer = container.querySelector("#preview-drawer-container") as HTMLElement;
          const buttonContainer = container.querySelector("#preview-button-container") as HTMLElement;

          if (!drawerContainer || !buttonContainer) {
            throw new Error("Preview containers not found");
          }

          // Create drawer wrapper div inside drawer container
          const drawerWrapperDiv = document.createElement("div");
          drawerWrapperDiv.style.cssText = "width: 100%; height: 100%;";
          drawerContainer.appendChild(drawerWrapperDiv);

          // Create button wrapper div inside button container
          const buttonWrapperDiv = document.createElement("div");
          buttonWrapperDiv.style.cssText = "position: relative; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;";
          buttonContainer.appendChild(buttonWrapperDiv);

          // Create drawer using preview module
          const drawerInstance = previewModule.createPreviewDrawer(
            drawerWrapperDiv,
            {
              shop,
              customerEmail: mockCustomerEmail,
              branding,
              colors: widgetColors,
              texts: widgetTexts,
              walletData: mockWalletData,
              appliedRewards: [mockActiveDiscount],
              loading: false,
              error: null,
              isWorking: false,
              showCloseButton: false,
            }
          );

          // Create button using preview module
          const buttonInstance = previewModule.createPreviewButton(
            buttonWrapperDiv,
            {
              position: 'right',
              isWorking: false,
              programName: branding.programName || 'Loyalty Program',
              currentPoints: mockWalletData.balance || 0,
              customerEmail: mockCustomerEmail,
              brandColor: branding.brandColor || '#006eff',
              isDrawerOpen: false,
            }
          );

          if (!isMounted) return;

          drawerRef.current = drawerInstance;
          buttonRef.current = buttonInstance;
          isInitializedRef.current = true;
          setIsLoading(false);
        } catch (err: any) {
          if (!isMounted) return;
          console.error("Failed to load preview:", err);
          setError(
            "Failed to load widget preview. Make sure the preview module is available."
          );
          setIsLoading(false);
        }
      } catch (err: any) {
        if (!isMounted) return;
        console.error("Error setting up widget preview:", err);
        setError(err.message || "Failed to set up widget preview");
        setIsLoading(false);
      }
    };

    initPreview();

    // Cleanup
    return () => {
      isMounted = false;
      if (drawerRef.current) {
        const instance = drawerRef.current as any;
        if (instance && typeof instance.$destroy === "function") {
          instance.$destroy();
        } else if (
          drawerRef.current instanceof HTMLElement &&
          drawerRef.current.parentNode
        ) {
          drawerRef.current.parentNode.removeChild(drawerRef.current);
        }
      }
      if (buttonRef.current) {
        const instance = buttonRef.current as any;
        if (instance && typeof instance.$destroy === "function") {
          instance.$destroy();
        } else if (
          buttonRef.current instanceof HTMLElement &&
          buttonRef.current.parentNode
        ) {
          buttonRef.current.parentNode.removeChild(buttonRef.current);
        }
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
      isInitializedRef.current = false;
    };
  }, [shop, hasRequiredData]); // Reinitialize if shop changes or required data becomes available

  // Update props when branding or colors change (without reinitializing)
  useEffect(() => {
    if (!isInitializedRef.current) return;

    // Update drawer props
    if (drawerRef.current) {
      const drawerInstance = drawerRef.current as any;
      // Svelte 5 uses $set to update props
      if (drawerInstance && typeof drawerInstance.$set === "function") {
        drawerInstance.$set({
          branding,
          colors: widgetColors,
        });
      }
    }

    // Update button props
    if (buttonRef.current) {
      const buttonInstance = buttonRef.current as any;
      if (buttonInstance && typeof buttonInstance.$set === "function") {
        buttonInstance.$set({
          programName: branding.programName || 'Loyalty Program',
          brandColor: branding.brandColor || '#006eff',
        });
      }
    }
  }, [branding, widgetColors]);

  return (
    <Card>
      <Box padding="400">
        <Text variant="headingMd" as="h2" tone="subdued">
          📱 Widget Preview
        </Text>
        <Text as="p" tone="subdued">
          Preview of how your loyalty widget will appear to customers
        </Text>

        <Box paddingBlockStart="400">
          {(isLoading || !hasRequiredData) && (
            <div
              style={{
                textAlign: "center",
              }}
            >
              <Spinner
                accessibilityLabel="Loading widget preview"
                size="large"
              />
              <p style={{ marginTop: "16px", color: "#6b7280" }}>
                {!hasRequiredData ? 'Loading branding and colors...' : 'Loading widget preview...'}
              </p>
            </div>
          )}

          {error && (
            <div
              style={{
                textAlign: "center",
                color: "#dc2626",
                padding: "20px",
              }}
            >
              <p>⚠️ {error}</p>
              <p
                style={{ fontSize: "12px", marginTop: "8px", color: "#6b7280" }}
              >
                Web components need to be built. This is a preview-only
                component.
              </p>
            </div>
          )}

          <div
            ref={containerRef}
            style={{
              position: "relative",
              width: "100%",
              minHeight: "600px",
              background: "#f5f5f5",
              borderRadius: "8px",
              overflow: "hidden",
              border: "1px solid #e1e3e5",
              display: isLoading || error || !hasRequiredData ? "none" : "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          ></div>
        </Box>
      </Box>
    </Card>
  );
};

export default WidgetPreview;
